import { Fragment, useMemo } from "react";
import type * as mapboxgl from "mapbox-gl";

import MapContext from "../../contexts/MapContext";
import FuelLoad3DTrees from "../layer_components/FuelLoad3DTrees";
import MapDimFilm from "../layer_components/MapDimFilm";
import type { Fuel3DItem } from "./useEntryLayerStack";

interface StoryFuel3DLayersProps {
  /** The story map (StoryMapCanvas.onMapReady); null until its style loads. */
  map: mapboxgl.Map | null;
  /** From the scene's layer stack (buildFuel3DItems). */
  items: Fuel3DItem[];
  /**
   * Draw the project's imagery-placed tree / shrub crowns as well as the
   * lattice planted from the tiles. Needs an authed session (the crowns
   * endpoint), so public readers pass false.
   */
  crownsEnabled: boolean;
  /** A stand finished a planting pass (print capture readiness). */
  onPlanted?: (itemId: string, complete: boolean) => void;
}

const noop = () => {};

/**
 * The project page's 3D fuel layer on a story map: one three.js stand (trees,
 * shrubs, ground fuel, grass) plus a basemap-muting film per configured
 * "fuel_3d" entry. Each stand plants from the entry's invisible fuel-tile
 * loader that buildEntryLayers declares on the canvas, so all this needs is
 * the map instance — handed to the shared layer components through the
 * MapContext they read on the project page. No 3D fire here: story
 * animations play frozen frame packs, not the live burn dataset.
 */
export default function StoryFuel3DLayers({
  map,
  items,
  crownsEnabled,
  onPlanted,
}: StoryFuel3DLayersProps) {
  const ctx = useMemo(
    () => ({ map, setMap: noop, hoverText: "", setHoverText: noop }),
    [map]
  );
  if (!map || items.length === 0) return null;
  return (
    <MapContext.Provider value={ctx}>
      {items.map((item) => (
        <Fragment key={item.id}>
          <FuelLoad3DTrees
            sourceId={item.sourceId}
            active
            dimFill={false}
            show={item.show}
            projectId={
              crownsEnabled && item.projectId !== null ? String(item.projectId) : null
            }
            onPlanted={onPlanted ? (complete) => onPlanted(item.id, complete) : undefined}
          />
          <MapDimFilm active={item.dimBasemap} fuelSourceId={item.sourceId} />
        </Fragment>
      ))}
    </MapContext.Provider>
  );
}
